import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Input, Space, Popconfirm, message, Select, Tag, Tooltip } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { toolsApi, dictionariesApi } from '../services/api-services';

const ToolList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [activeTag, setActiveTag] = useState<string>('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const filteredData = activeTag ? data.filter((item: any) => item.tags?.includes(activeTag)) : data;

  const fetchData = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const res: any = await toolsApi.list({ page, page_size: size });
      setData(res.items);
      setTotal(res.total);
    } catch (e) { message.error('获取列表失败'); }
    setLoading(false);
  };

  const fetchTags = async () => {
    try {
      const dict: any = await dictionariesApi.getByCode('resource_tag');
      if (dict?.items) setTagOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch {}
  };

  useEffect(() => { fetchData(); fetchTags(); }, []);

  const handleSave = async (values: any) => {
    try {
      const res: any = await toolsApi.create(values);
      message.success('创建成功');
      setModalVisible(false);
      form.resetFields();
      navigate(`/tools/${res.id}`);
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleDelete = async (id: number) => {
    try { await toolsApi.delete(id); message.success('删除成功'); fetchData(); }
    catch (e) { message.error('删除失败'); }
  };

  const columns = [
    { title: '标识', dataIndex: 'identifier', key: 'identifier', width: 160 },
    { title: '名称', dataIndex: 'title', key: 'title' },
    { title: '描述', dataIndex: 'description', key: 'description', width: 300, render: (text: string) => {
      if (!text) return '-';
      const maxLen = 50;
      return text.length > maxLen ? <Tooltip title={text}>{text.slice(0, maxLen)}...</Tooltip> : text;
    }},
    { title: '标签', dataIndex: 'tags', key: 'tags', render: (tags: string[]) => tags?.map((t: string) => <Tag key={t} style={{ margin: '2px 4px 2px 0' }}>{tagOptions.find(o => o.value === t)?.label || t}</Tag>) },
    { title: '调用方式', dataIndex: 'call_method', key: 'call_method', render: (m: string) => (
      <span className={`apple-tag apple-tag-${m === 'python' ? 'blue' : 'green'}`}>{m}</span>
    )},
    { title: '版本', dataIndex: 'version', key: 'version', render: (v: number) => <span className="apple-tag">v{v || 1}</span> },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', render: (v: string) => v ? new Date(v).toLocaleString() : '-' },
    { title: '操作', key: 'action', width: 160, fixed: 'right' as const, align: 'center' as const, render: (_: any, record: any) => (
      <Space>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/tools/${record.id}`)}>编辑</Button>
        <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.id)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">Tool 工具</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalVisible(true); }}>新建 Tool</Button>
      </div>
      <p className="apple-page-subtitle">管理可执行工具，定义调用方式与脚本</p>

      {tagOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Tag style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }} color={activeTag === '' ? 'blue' : undefined} onClick={() => setActiveTag('')}>全部</Tag>
          {tagOptions.map((opt) => (
            <Tag key={opt.value} style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }} color={activeTag === opt.value ? 'blue' : undefined} onClick={() => setActiveTag(activeTag === opt.value ? '' : opt.value)}>{opt.label}</Tag>
          ))}
        </div>
      )}

      <ResponsiveTable columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t: number) => `共 ${t} 条`, onChange: (page: number, size: number) => { setCurrentPage(page); setPageSize(size); fetchData(page, size); } }} />

      <Modal
        className="apple-modal"
        title="新建 Tool"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={640}
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="identifier" label="标识" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和连字符' }, { max: 64, message: '最长64字符' }]}><Input placeholder="如 my-tool-name" /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="call_method" label="调用方式" rules={[{ required: true }]}>
            <Select options={[{ label: 'Python', value: 'python' }, { label: 'Shell', value: 'shell' }]} />
          </Form.Item>
          <Form.Item name="script_name" label="脚本文件名" rules={[{ required: true, message: '请填写脚本文件名' }]}>
            <Input placeholder="如 main.py、run.sh" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Select mode="multiple" placeholder="选择标签" options={tagOptions} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ToolList;
